/**
 * @file Tooltip 悬停说明
 * @description 自定义浮层；fade；视口边界翻转；可选 interactive 以支持移入浮层滚动/交互。
 *
 * @whenToUse 图标按钮说明、截断文本完整内容。
 * @whenNotToUse 可交互菜单（用 DropdownMenu / Popover）。
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn.ts'
import {
  computeTooltipBox,
  type TooltipPlacement,
} from '@/lib/floating.ts'

/** 离开触发器后，允许移入浮层的宽限时间（ms） */
const INTERACTIVE_CLOSE_DELAY = 120

export type TooltipProps = {
  /** 提示内容 */
  content: ReactNode
  /** 触发元素 */
  children: ReactNode
  className?: string
  /**
   * 是否可移入浮层交互（滚动、选中文字等）。
   * 开启后浮层接收指针事件，离开触发器后短暂延迟再关闭，便于移入浮层。
   */
  interactive?: boolean
  /**
   * 浮层相对触发器的方位。
   * - `auto`：优先上方、水平居中（默认）
   * - `top` / `bottom`：上下居中
   * - `top-left` / `top-right` / `bottom-left` / `bottom-right`：角落对齐
   */
  placement?: TooltipPlacement
}

export function Tooltip({
  content,
  children,
  className,
  interactive = false,
  placement = 'auto',
}: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const tipId = useId()
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  function clearCloseTimer() {
    if (closeTimerRef.current == null) return
    window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }

  function openTip() {
    clearCloseTimer()
    setOpen(true)
  }

  function scheduleClose() {
    if (!interactive) {
      setOpen(false)
      return
    }
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false)
      closeTimerRef.current = null
    }, INTERACTIVE_CLOSE_DELAY)
  }

  const update = () => {
    const trigger = triggerRef.current
    const tip = tipRef.current
    if (!trigger || !tip) return
    const box = computeTooltipBox(
      trigger.getBoundingClientRect(),
      tip.getBoundingClientRect(),
      { placement },
    )
    setPos({ top: box.top, left: box.left })
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  /** portal 挂载后再量尺寸并定位，避免 tipRef 为空时沿用旧坐标 */
  useLayoutEffect(() => {
    if (!open || !mounted) return
    update()
    const id = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(id)
  }, [open, mounted, content, placement])

  useEffect(() => {
    if (!open) return
    const onWin = () => update()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, placement])

  useEffect(() => () => clearCloseTimer(), [])

  return (
    <>
      <span
        ref={triggerRef}
        className={cn('inline-flex max-w-full min-w-0', className)}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={openTip}
        onMouseLeave={scheduleClose}
        onFocus={openTip}
        onBlur={scheduleClose}
      >
        {children}
      </span>
      {mounted && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={tipRef}
              id={tipId}
              role="tooltip"
              className={cn(
                'fixed z-50 max-w-sm rounded-md border border-border bg-raised px-2 py-1.5 text-xs text-foreground shadow-md transition-opacity duration-200',
                interactive ? 'pointer-events-auto' : 'pointer-events-none',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={{ top: pos.top, left: pos.left }}
              onMouseEnter={interactive ? openTip : undefined}
              onMouseLeave={interactive ? scheduleClose : undefined}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
